import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GraduationCap, X, Volume2, Pause, Send, Sparkles, Flag } from "lucide-react";
import { toast } from "sonner";
import { tutorContext } from "@/lib/companion";
import { useAttentionSettings } from "@/lib/attentionSettings";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * <AITeacher /> — floating tutor button + slide-in chat panel.
 *
 * Auto-loads a warm INTRO for the current learning outcome the moment
 * the panel opens (cached server-side by `code + age_tier`). Every AI
 * message auto-plays via /api/tts (nova voice by default) and can be
 * replayed with a tap. Kids can ask follow-up questions capped at 300
 * chars — the LLM stays scoped to the objective.
 */
export default function AITeacher(props) {
  const { aiDisabled } = useAttentionSettings();
  if (aiDisabled) return null; // guardian switch (/ai-safety)
  return <AITeacherInner {...props} />;
}

function AITeacherInner({ code, outcome, subject, ageTier = "8-10", questionIndex = -1 }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // {who:'teacher'|'child', text}
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  // Cycle 56 · Part 4B — Socratic hint escalation. Rises with each "help"/"I don't know" style ask.
  const [hintLevel, setHintLevel] = useState(0);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    if (!open || messages.length) return;
    // Auto-fetch intro on first open.
    (async () => {
      setBusy(true);
      try {
        const res = await fetch(`${API}/api/teacher`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code, outcome, subject, age_tier: ageTier, mode: "intro" }),
        });
        if (!res.ok) throw new Error(`teacher ${res.status}`);
        const data = await res.json();
        setMessages([{ who: "teacher", text: data.text }]);
        speak(data.text);
      } catch {
        setMessages([{ who: "teacher", text: "Sorry — I couldn't load your lesson right now. Try again in a moment." }]);
      } finally { setBusy(false); }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const speak = async (text) => {
    if (!text) return;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      const res = await fetch(`${API}/api/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 550), voice: "nova", speed: 0.95 }),
      });
      if (!res.ok) throw new Error(`tts ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = new Audio(url);
      audioRef.current = a;
      a.onplay = () => setPlaying(true);
      a.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      a.onerror = () => { setPlaying(false); URL.revokeObjectURL(url); };
      await a.play();
    } catch {
      // Silent audio failure — user can still read the text.
    }
  };

  const stop = () => {
    if (audioRef.current) { audioRef.current.pause(); setPlaying(false); }
  };

  const send = async () => {
    const text = q.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { who: "child", text }]);
    setQ("");
    setBusy(true);
    // Cycle 56 · Part 4B — detect "I still need help" cues to bump hint level.
    const stuckWords = /(help|hint|stuck|don'?t\s*(know|understand|get)|another|more)/i;
    const nextHint = stuckWords.test(text) ? Math.min(3, hintLevel + 1) : 0;
    setHintLevel(nextHint);
    try {
      const res = await fetch(`${API}/api/teacher`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code, outcome, subject,
          age_tier: ageTier, mode: "ask", question: text,
          hint_level: nextHint,
          learner_context: tutorContext().slice(0, 700),
        }),
      });
      if (!res.ok) throw new Error(`ask ${res.status}`);
      const data = await res.json();
      setMessages((m) => [...m, { who: "teacher", text: data.text }]);
      speak(data.text);
    } catch {
      setMessages((m) => [...m, { who: "teacher", text: "Hmm, I couldn't answer that just now. Try again?" }]);
    } finally { setBusy(false); }
  };

  const flag = async (reason) => {
    try {
      await fetch(`${API}/api/quiz-feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, question_index: Math.max(0, questionIndex), reason }),
      });
      toast.success("Thanks! We'll refresh this question.");
    } catch {
      toast("Couldn't send the flag right now.");
    }
  };

  useEffect(() => () => { if (audioRef.current) audioRef.current.pause(); }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="ai-teacher-open"
        className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full border-[3px] border-slate-900 bg-sky-200 font-bold text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-all"
      >
        <GraduationCap size={16} strokeWidth={3} /> Ask the teacher
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[90] bg-slate-900/55 flex items-end sm:items-center justify-center p-0 sm:p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            data-testid="ai-teacher-overlay"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              data-testid="ai-teacher-panel"
              className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl border-4 border-slate-900 bg-white shadow-[10px_10px_0_0_rgba(15,23,42,1)] flex flex-col max-h-[85vh]"
            >
              <header className="p-4 border-b-[3px] border-slate-900 bg-gradient-to-r from-sky-200 to-yellow-100 rounded-t-[20px] flex items-center gap-2">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)]">
                  <GraduationCap size={18} strokeWidth={3} />
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-sky-800">AI Teacher</p>
                  <p className="font-heading text-base font-bold leading-tight truncate">Lesson: {code}</p>
                </div>
                <button
                  type="button" onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid="ai-teacher-close"
                  className="h-9 w-9 rounded-full border-[3px] border-slate-900 bg-white inline-flex items-center justify-center"
                >
                  <X size={16} strokeWidth={3} />
                </button>
              </header>

              <div className="flex-1 overflow-y-auto p-3 space-y-2" data-testid="ai-teacher-chat">
                {messages.map((m, i) => (
                  <div key={i} className={m.who === "teacher" ? "flex gap-2" : "flex gap-2 justify-end"}>
                    {m.who === "teacher" && (
                      <span aria-hidden className="text-xl leading-none">🌍</span>
                    )}
                    <div className={
                      "rounded-2xl border-[3px] border-slate-900 px-3 py-2 text-sm font-bold leading-snug shadow-[0_2px_0_0_rgba(15,23,42,1)] max-w-[80%] " +
                      (m.who === "teacher" ? "bg-yellow-50" : "bg-emerald-100")
                    }>
                      <p>{m.text}</p>
                      {m.who === "teacher" && (
                        <div className="mt-1.5 flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => (playing ? stop() : speak(m.text))}
                            data-testid={`ai-teacher-play-${i}`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase tracking-wider text-sky-800"
                          >
                            {playing ? <Pause size={11} strokeWidth={3} /> : <Volume2 size={11} strokeWidth={3} />}
                            {playing ? "Stop" : "Listen"}
                          </button>
                          {questionIndex >= 0 && (
                            <button
                              type="button"
                              onClick={() => flag("confusing")}
                              className="inline-flex items-center gap-0.5 text-[10px] font-black uppercase tracking-wider text-rose-800"
                              title="Flag this question"
                            >
                              <Flag size={11} strokeWidth={3} /> Flag
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex gap-2 items-center text-xs font-bold text-slate-500">
                    <Sparkles size={14} strokeWidth={3} className="animate-pulse" /> thinking…
                  </div>
                )}
              </div>

              <div className="p-3 border-t-[3px] border-slate-900 bg-yellow-50 flex gap-2 rounded-b-3xl">
                <input
                  type="text" value={q} onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                  maxLength={300}
                  placeholder="Ask a question..."
                  data-testid="ai-teacher-input"
                  className="flex-1 rounded-full border-[3px] border-slate-900 px-3 py-2 font-bold text-sm shadow-[0_2px_0_0_rgba(15,23,42,1)]"
                />
                <button
                  type="button" onClick={send}
                  disabled={busy || !q.trim()}
                  data-testid="ai-teacher-send"
                  className="h-11 w-11 rounded-full border-[3px] border-slate-900 bg-emerald-300 font-bold shadow-[0_3px_0_0_rgba(15,23,42,1)] inline-flex items-center justify-center disabled:opacity-40"
                >
                  <Send size={16} strokeWidth={3} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
